/**
 * ============================================================================
 * SystemConfig 模块 - 默认配置定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义所有配置项的默认值
 * - 基于后端 SystemConfigSeeder 的实际配置
 * - 为表单化配置系统提供默认值参考
 * 
 * 📖 对应后端：
 * - Seeder: backend/database/seeders/Core/SystemConfigSeeder.php
 * - Model: backend/app/Modules/System/Models/SystemConfig.php
 * 
 * ⚠️ 注意事项：
 * - 默认值必须与后端 SystemConfigSeeder 中的值完全一致
 * - 配置键名格式：{group}.{key}
 * - 配置值类型必须与 config_type 匹配
 * - 新增配置项时必须同步更新此文件
 * 
 * 🔄 更新时机：
 * - 后端新增配置项时
 * - 后端修改默认值时
 * - 业务需求变更时
 * 
 * ✅ 验证方式：
 * 让AI执行以下检查：
 * 1. 读取 backend/database/seeders/Core/SystemConfigSeeder.php
 * 2. 对比每个配置项的默认值是否一致
 * 3. 检查是否有遗漏或多余的配置项
 * 4. 验证配置键名格式是否正确
 * 
 * @module types/system-config/defaults
 * @generated AI-assisted
 * @last-updated 2025-10-22
 * ============================================================================
 */

import { ConfigGroup } from './enums'

/**
 * 系统配置默认值
 * 
 * 对应后端：SystemConfigSeeder 中的 system 分组配置
 */
export const SYSTEM_CONFIG_DEFAULTS = {
  /** 是否启用缓存 */
  cache_enabled: true,
  /** 默认缓存时间（秒） */
  cache_ttl: 3600,
  /** 是否限制管理员和高权限账号在 App 记住密码 */
  remember_password_restrict_privileged_accounts: false,
  /** Redis同步频道 */
  redis_channel: 'mes_config_updates',
} as const

/**
 * 销售配置默认值
 * 
 * 对应后端：SystemConfigSeeder 中的 sales 分组配置
 */
export const SALES_CONFIG_DEFAULTS = {
  /** 订单号前缀 */
  order_number_prefix: 'SH',
  /** 客户编号长度 */
  customer_code_length: 5,
  /** 订单号日期时间格式 */
  order_number_datetime_format: 'YmdHis',
  /** 订单号序号长度 */
  order_number_sequence_length: 4,
  /** 是否自动生成订单号 */
  auto_generate_order_number: true,
} as const

/**
 * 客户配置默认值
 * 
 * 对应后端：SystemConfigSeeder 中的 customer 分组配置
 */
export const CUSTOMER_CONFIG_DEFAULTS = {
  /** 是否自动生成客户编号 */
  code_auto_generate: true,
  /** 客户编号前缀 */
  code_prefix: 'C',
} as const

/**
 * 物料配置默认值
 * 
 * 对应后端：SystemConfigSeeder 中的 material 分组配置
 */
export const MATERIAL_CONFIG_DEFAULTS = {
  /** 是否自动生成物料编号 */
  code_auto_generate: true,
  /** 物料编号前缀 */
  code_prefix: 'M',
} as const

/**
 * 库存配置默认值
 * 
 * 对应后端：SystemConfigSeeder 中的 inventory 分组配置
 */
export const INVENTORY_CONFIG_DEFAULTS = {
  /** 是否自动扣减库存 */
  auto_deduct: true,
  /** 是否允许负库存 */
  allow_negative: false,
} as const

/**
 * 生产配置默认值
 * 
 * 对应后端：SystemConfigSeeder 中的 production 分组配置
 */
export const PRODUCTION_CONFIG_DEFAULTS = {
  /** 工单号前缀 */
  work_order_prefix: 'WO',
  /** 是否自动开始生产流程 */
  auto_start_process: false,
  /** 报工可撤销时限（分钟），后端撤销报工校验消费，未配置默认 1440（24 小时） */
  report_deadline_minutes: 1440,
} as const

/**
 * 质量配置默认值
 * 
 * 对应后端：SystemConfigSeeder 中的 quality 分组配置
 */
export const QUALITY_CONFIG_DEFAULTS = {
  /** 是否必需质检 */
  inspection_required: true,
  /** 自动通过质检阈值（百分比） */
  auto_pass_threshold: 95,
} as const

/**
 * 所有配置分组的默认值映射
 * 
 * 用于根据配置分组快速获取对应的默认值
 */
export const CONFIG_GROUP_DEFAULTS = {
  [ConfigGroup.SYSTEM]: SYSTEM_CONFIG_DEFAULTS,
  [ConfigGroup.ORDER]: {}, // 订单配置默认值（待实现）
  [ConfigGroup.SALES]: SALES_CONFIG_DEFAULTS,
  [ConfigGroup.CUSTOMER]: CUSTOMER_CONFIG_DEFAULTS,
  [ConfigGroup.MATERIAL]: MATERIAL_CONFIG_DEFAULTS,
  [ConfigGroup.INVENTORY]: INVENTORY_CONFIG_DEFAULTS,
  [ConfigGroup.PRODUCTION]: PRODUCTION_CONFIG_DEFAULTS,
  [ConfigGroup.QUALITY]: QUALITY_CONFIG_DEFAULTS,
  [ConfigGroup.BOM]: {}, // BOM配置默认值（待实现）
  [ConfigGroup.NOTIFICATION]: {}, // 通知配置默认值（待实现）
  [ConfigGroup.API]: {}, // API配置默认值（待实现）
  [ConfigGroup.AUDIT]: {}, // 审计配置默认值（待实现）
} as const

/**
 * 获取指定分组的默认配置
 * 
 * @param group - 配置分组
 * @returns 该分组的默认配置对象
 * 
 * @example
 * ```typescript
 * // 获取系统配置默认值
 * const systemDefaults = getConfigGroupDefaults(ConfigGroup.SYSTEM)
 * console.log(systemDefaults.cache_enabled) // true
 * 
 * // 获取销售配置默认值
 * const salesDefaults = getConfigGroupDefaults(ConfigGroup.SALES)
 * console.log(salesDefaults.order_number_prefix) // 'SH'
 * ```
 */
export function getConfigGroupDefaults(group: ConfigGroup): Record<string, any> {
  return (CONFIG_GROUP_DEFAULTS as any)[group] || {}
}

/**
 * 获取所有配置项的默认值（扁平化）
 * 
 * 将所有分组的默认配置合并为一个对象，键名格式为 {group}.{key}
 * 
 * @returns 所有配置项的默认值对象
 * 
 * @example
 * ```typescript
 * const allDefaults = getAllConfigDefaults()
 * console.log(allDefaults['system.cache_enabled']) // true
 * console.log(allDefaults['sales.order_number_prefix']) // 'SH'
 * ```
 */
export function getAllConfigDefaults(): Record<string, any> {
  const allDefaults: Record<string, any> = {}
  
  Object.entries(CONFIG_GROUP_DEFAULTS).forEach(([group, configs]) => {
    Object.entries(configs).forEach(([key, value]) => {
      allDefaults[`${group}.${key}`] = value
    })
  })
  
  return allDefaults
}

/**
 * 配置项类型定义
 * 
 * 基于实际后端配置项的类型定义
 */
export interface SystemConfigDefaults {
  cache_enabled: boolean
  cache_ttl: number
  redis_channel: string
}

export interface SalesConfigDefaults {
  order_number_prefix: string
  customer_code_length: number
  order_number_datetime_format: string
  order_number_sequence_length: number
  auto_generate_order_number: boolean
}

export interface CustomerConfigDefaults {
  code_auto_generate: boolean
  code_prefix: string
}

export interface MaterialConfigDefaults {
  code_auto_generate: boolean
  code_prefix: string
}

export interface InventoryConfigDefaults {
  auto_deduct: boolean
  allow_negative: boolean
}

export interface ProductionConfigDefaults {
  work_order_prefix: string
  auto_start_process: boolean
}

export interface QualityConfigDefaults {
  inspection_required: boolean
  auto_pass_threshold: number
}
